// Google Analytics 4 E-commerce Tracking Helper
// Documentation: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce

const GA_MEASUREMENT_ID = 'G-11V26DZCLK';

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Track page view
export const trackPageView = (pagePath, pageTitle) => {
  if (!isGtagAvailable()) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle
  });
};

// Track view_item event - when user views a product
export const trackViewItem = (product) => {
  if (!isGtagAvailable() || !product) return;
  
  window.gtag('event', 'view_item', {
    currency: 'RON',
    value: product.price || 0,
    items: [{
      item_id: product._id || product.id || product.sourceId,
      item_name: product.name,
      item_category: product.category || '',
      item_brand: product.brand || product.supplierId || '',
      price: product.price || 0,
      quantity: 1
    }]
  });
  
  console.log('[GA4] view_item tracked:', product.name);
};

// Track add_to_cart event
export const trackAddToCart = (product, quantity = 1) => {
  if (!isGtagAvailable() || !product) return;
  
  const value = (product.price || 0) * quantity;
  
  window.gtag('event', 'add_to_cart', {
    currency: 'RON',
    value: value,
    items: [{
      item_id: product._id || product.id || product.sourceId,
      item_name: product.name,
      item_category: product.category || '',
      item_brand: product.brand || product.supplierId || '',
      price: product.price || 0,
      quantity: quantity
    }]
  });
  
  console.log('[GA4] add_to_cart tracked:', product.name, 'qty:', quantity);
};

// Track remove_from_cart event
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!isGtagAvailable() || !product) return;
  
  const value = (product.price || 0) * quantity;
  
  window.gtag('event', 'remove_from_cart', {
    currency: 'RON',
    value: value,
    items: [{
      item_id: product.productId || product._id || product.id,
      item_name: product.name,
      price: product.price || 0,
      quantity: quantity
    }]
  });
  
  console.log('[GA4] remove_from_cart tracked:', product.name);
};

// Track view_cart event
export const trackViewCart = (cartItems) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;
  
  const value = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  window.gtag('event', 'view_cart', {
    currency: 'RON',
    value: value,
    items: cartItems.map(item => ({
      item_id: item.productId || item._id || item.id,
      item_name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1
    }))
  });
  
  console.log('[GA4] view_cart tracked, total:', value);
};

// Track begin_checkout event
export const trackBeginCheckout = (cartItems, coupon = null) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;
  
  const value = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const eventData = {
    currency: 'RON',
    value: value,
    items: cartItems.map(item => ({
      item_id: item.productId || item._id || item.id,
      item_name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1
    }))
  };
  
  if (coupon) {
    eventData.coupon = coupon;
  }
  
  window.gtag('event', 'begin_checkout', eventData);
  
  console.log('[GA4] begin_checkout tracked, total:', value);
};

// Track add_shipping_info event
export const trackAddShippingInfo = (cartItems, shippingMethod, shippingCost) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;
  
  const value = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  window.gtag('event', 'add_shipping_info', {
    currency: 'RON',
    value: value,
    shipping_tier: shippingMethod || 'standard',
    items: cartItems.map(item => ({
      item_id: item.productId || item._id || item.id,
      item_name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1
    }))
  });
  
  console.log('[GA4] add_shipping_info tracked:', shippingMethod);
};

// Track add_payment_info event
export const trackAddPaymentInfo = (cartItems, paymentMethod) => {
  if (!isGtagAvailable() || !cartItems || cartItems.length === 0) return;
  
  const value = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  window.gtag('event', 'add_payment_info', {
    currency: 'RON',
    value: value,
    payment_type: paymentMethod || 'cash',
    items: cartItems.map(item => ({
      item_id: item.productId || item._id || item.id,
      item_name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1
    }))
  });
  
  console.log('[GA4] add_payment_info tracked:', paymentMethod);
};

// Track purchase event - most important conversion event
export const trackPurchase = (orderData) => {
  if (!isGtagAvailable() || !orderData) return;
  
  const { orderId, items, total, shipping, coupon } = orderData;
  
  const eventData = {
    transaction_id: orderId,
    currency: 'RON',
    value: total || 0,
    shipping: shipping || 0,
    tax: 0, // TVA is included in price in Romania
    items: (items || []).map(item => ({
      item_id: item.productId || item._id || item.id,
      item_name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1
    }))
  };
  
  if (coupon) {
    eventData.coupon = coupon;
  }
  
  window.gtag('event', 'purchase', eventData);
  
  console.log('[GA4] purchase tracked, order:', orderId, 'total:', total);
};

// Track search event
export const trackSearch = (searchTerm) => {
  if (!isGtagAvailable() || !searchTerm) return;
  
  window.gtag('event', 'search', {
    search_term: searchTerm
  });
  
  console.log('[GA4] search tracked:', searchTerm);
};

// Track view_item_list event - when viewing product list/category
export const trackViewItemList = (items, listName = 'Product List') => {
  if (!isGtagAvailable() || !items || items.length === 0) return;
  
  window.gtag('event', 'view_item_list', {
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    item_list_name: listName,
    items: items.slice(0, 10).map((item, index) => ({
      item_id: item._id || item.id || item.sourceId,
      item_name: item.name,
      item_category: item.category || '',
      price: item.price || 0,
      index: index
    }))
  });
  
  console.log('[GA4] view_item_list tracked:', listName, 'items:', items.length);
};

// Track select_item event - when clicking on a product from list
export const trackSelectItem = (product, listName = 'Product List') => {
  if (!isGtagAvailable() || !product) return;
  
  window.gtag('event', 'select_item', {
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    item_list_name: listName,
    items: [{
      item_id: product._id || product.id || product.sourceId,
      item_name: product.name,
      item_category: product.category || '',
      price: product.price || 0
    }]
  });
  
  console.log('[GA4] select_item tracked:', product.name);
};

// Track login event
export const trackLogin = (method = 'email') => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'login', {
    method: method
  });
  
  console.log('[GA4] login tracked:', method);
};

// Track sign_up event
export const trackSignUp = (method = 'email') => {
  if (!isGtagAvailable()) return;
  
  window.gtag('event', 'sign_up', {
    method: method
  });
  
  console.log('[GA4] sign_up tracked:', method);
};

// Track add_to_wishlist event
export const trackAddToWishlist = (product) => {
  if (!isGtagAvailable() || !product) return;
  
  window.gtag('event', 'add_to_wishlist', {
    currency: 'RON',
    value: product.price || 0,
    items: [{
      item_id: product._id || product.id || product.sourceId,
      item_name: product.name,
      item_category: product.category || '',
      price: product.price || 0
    }]
  });
  
  console.log('[GA4] add_to_wishlist tracked:', product.name);
};

export default {
  trackPageView,
  trackViewItem,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackSearch,
  trackViewItemList,
  trackSelectItem,
  trackLogin,
  trackSignUp,
  trackAddToWishlist
};
